import { bufferToBase64url, base64urlToBuffer } from './base64url';

/**
 * Fixed, non-secret salt for the WebAuthn PRF extension eval. PRF security comes from the
 * authenticator-bound secret function, not from salt secrecy — this only needs to be stable
 * across calls so the same passkey always derives the same wrapping key.
 */
const PRF_SALT_LABEL = 'token-lean-ide:prf-wrap-salt:v1';

let cachedPrfSalt: ArrayBuffer | null = null;
async function prfSalt(): Promise<ArrayBuffer> {
  if (!cachedPrfSalt) {
    cachedPrfSalt = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(PRF_SALT_LABEL));
  }
  return cachedPrfSalt;
}

/** The `extensions.prf.eval.first` value to request on both create() and get() ceremonies. */
export async function prfEvalFirst(): Promise<ArrayBuffer> {
  return prfSalt();
}

/** Generates a random 256-bit AES-GCM master key (K) — extractable so it can be wrapped per-passkey. */
export async function generateMasterKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/** Derives a non-extractable AES-GCM wrapping key from a passkey's raw PRF output via HKDF. */
async function deriveWrappingKeyFromPrf(prfOutput: ArrayBuffer): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey('raw', prfOutput, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: await prfSalt(), info: new TextEncoder().encode('master-key-wrap') },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

export interface WrappedKeyMaterial {
  wrappedMasterKey: string; // base64url ciphertext
  wrapIv: string; // base64url IV
}

/** Wraps master key K under a wrapping key derived from this passkey's PRF output. */
export async function wrapMasterKey(masterKey: CryptoKey, prfOutput: ArrayBuffer): Promise<WrappedKeyMaterial> {
  const wrappingKey = await deriveWrappingKeyFromPrf(prfOutput);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await crypto.subtle.wrapKey('raw', masterKey, wrappingKey, { name: 'AES-GCM', iv });
  return { wrappedMasterKey: bufferToBase64url(wrapped), wrapIv: bufferToBase64url(iv) };
}

/** Unwraps master key K using a wrapping key derived from this passkey's PRF output. */
export async function unwrapMasterKey(material: WrappedKeyMaterial, prfOutput: ArrayBuffer): Promise<CryptoKey> {
  const wrappingKey = await deriveWrappingKeyFromPrf(prfOutput);
  const iv = base64urlToBuffer(material.wrapIv) as BufferSource;
  return crypto.subtle.unwrapKey(
    'raw',
    base64urlToBuffer(material.wrappedMasterKey) as BufferSource,
    wrappingKey,
    { name: 'AES-GCM', iv },
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
}

export interface EncryptedPayload {
  ciphertext: string; // base64url
  iv: string; // base64url
}

export async function encryptWithMasterKey(masterKey: CryptoKey, plaintext: string): Promise<EncryptedPayload> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    masterKey,
    new TextEncoder().encode(plaintext),
  );
  return { ciphertext: bufferToBase64url(ciphertext), iv: bufferToBase64url(iv) };
}

export async function decryptWithMasterKey(masterKey: CryptoKey, payload: EncryptedPayload): Promise<string> {
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64urlToBuffer(payload.iv) as BufferSource },
    masterKey,
    base64urlToBuffer(payload.ciphertext) as BufferSource,
  );
  return new TextDecoder().decode(plaintext);
}
